import type { ReactNode } from "react";

import { useEffect, useState } from "react";

import type { DropdownOption } from "../../../DropDownV2";
import type { MultiSelectOption } from "../../../MultiSelect";
import type { SegmentOption } from "../../../SegmentControl";
import type { ReturnedTranslatedContentClientsPageHook } from "../../types";

import { Button as ButtonV2 } from "../../../Button";
import { MultiSelect } from "../../../MultiSelect";
import { ResponsiveModal } from "../../../ResponsiveModal";
import { SegmentControl } from "../../../SegmentControl";
import { Typo } from "../../../Typo";

type FilterDimension = "rebateProgram" | "rebateAccount";

// Neither dimension has a default selection -- rebate account only becomes
// "the active dimension" once the user has actually picked one, symmetric
// with rebate program's empty-is-off state.
const deriveInitialDimension = (
  selectedRebateAccounts: DropdownOption[],
): FilterDimension =>
  selectedRebateAccounts.length > 0 ? "rebateAccount" : "rebateProgram";

export type ClientAccountFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rebateAccountOptions: DropdownOption[];
  selectedRebateAccounts: DropdownOption[];
  onRebateAccountsChange: (options: DropdownOption[]) => void;
  rebateProgramOptions: MultiSelectOption[];
  selectedRebatePrograms: MultiSelectOption[];
  onRebateProgramsChange: (options: MultiSelectOption[]) => void;
  translations: ReturnedTranslatedContentClientsPageHook;
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
};

const Field = ({
  label,
  hint,
  children,
}: {
  label?: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    {label && (
      <Typo element="span" type="labelSM" textColor="text-text-primary">
        {label}
      </Typo>
    )}
    {hint && (
      <Typo
        element="p"
        type="bodyXS"
        textColor="text-text-secondary"
        className="mb-1"
      >
        {hint}
      </Typo>
    )}
    {children}
  </div>
);

// Fields are edited as a local draft and only committed to the real report
// context (which triggers a refetch) when the user clicks Apply -- Clear only
// resets the draft, it doesn't filter anything until Apply is pressed too.
// Account Report has no date range filter, so this modal only ever groups the
// Rebate program / Rebate account dimension toggle -- mirrors
// ClientTradingReport/Filters/FilterModal.tsx minus the date field.
export const ClientAccountFilterModal = ({
  isOpen,
  onClose,
  rebateAccountOptions,
  selectedRebateAccounts,
  onRebateAccountsChange,
  rebateProgramOptions,
  selectedRebatePrograms,
  onRebateProgramsChange,
  translations: { translatedContent },
  showRebateProgramFilter,
}: ClientAccountFilterModalProps) => {
  const [draftRebateAccounts, setDraftRebateAccounts] = useState(
    selectedRebateAccounts,
  );
  const [draftRebatePrograms, setDraftRebatePrograms] = useState(
    selectedRebatePrograms,
  );
  const [dimension, setDimension] = useState<FilterDimension>(() =>
    deriveInitialDimension(selectedRebateAccounts),
  );

  // Re-seed the draft from the live filters whenever the modal (re)opens.
  useEffect(() => {
    if (!isOpen) return;
    setDraftRebateAccounts(selectedRebateAccounts);
    setDraftRebatePrograms(selectedRebatePrograms);
    setDimension(deriveInitialDimension(selectedRebateAccounts));
  }, [isOpen, selectedRebateAccounts, selectedRebatePrograms]);

  // Rebate program and rebate account are an either/or -- switching clears
  // the other dimension's draft back to its neutral/off state so only one of
  // the pair is ever committed on Apply.
  const switchDimension = (next: FilterDimension) => {
    if (next === dimension) return;
    if (next === "rebateProgram") {
      setDraftRebateAccounts([]);
    } else {
      setDraftRebatePrograms([]);
    }
    setDimension(next);
  };

  const handleClear = () => {
    setDraftRebateAccounts([]);
    setDraftRebatePrograms([]);
  };

  const handleApply = () => {
    onRebateAccountsChange(draftRebateAccounts);
    onRebateProgramsChange(draftRebatePrograms);
    onClose();
  };

  const rebateProgramField = (
    <Field
      label={translatedContent.filters?.rebatePrograms}
      hint={translatedContent.filters?.rebateProgramsHint}
    >
      <MultiSelect
        modal
        id="rebateProgramFilterModal"
        options={rebateProgramOptions}
        selected={draftRebatePrograms}
        onChange={setDraftRebatePrograms}
        placeholder={translatedContent.filters?.rebateProgramsPlaceholder}
      />
    </Field>
  );

  const rebateAccountField = (
    <Field
      label={translatedContent.filters?.rebateAccounts}
      hint={translatedContent.filters?.rebateAccountsHint}
    >
      <MultiSelect
        modal
        id="rebateAccountFilterModal"
        options={rebateAccountOptions}
        selected={draftRebateAccounts}
        onChange={setDraftRebateAccounts}
        placeholder={translatedContent.filters?.rebateAccountsPlaceholder}
      />
    </Field>
  );

  const dimensionOptions: SegmentOption<FilterDimension>[] = [
    {
      label: translatedContent.filters?.rebatePrograms ?? "",
      value: "rebateProgram",
    },
    {
      label: translatedContent.filters?.rebateAccounts ?? "",
      value: "rebateAccount",
    },
  ];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      isMobile={false}
      title={translatedContent.filters?.filterButton ?? "Filter"}
      contentOverflow="visible"
      footer={
        <div className="flex justify-end gap-3">
          <ButtonV2 variant="outline" size="md" onClick={handleClear}>
            {translatedContent.filters?.clear ?? "Clear"}
          </ButtonV2>
          <ButtonV2 variant="brand" size="md" onClick={handleApply}>
            {translatedContent.apply}
          </ButtonV2>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {showRebateProgramFilter ? (
          <>
            <SegmentControl
              fullWidth
              options={dimensionOptions}
              value={dimension}
              onChange={switchDimension}
            />
            {dimension === "rebateProgram"
              ? rebateProgramField
              : rebateAccountField}
          </>
        ) : (
          rebateAccountField
        )}
      </div>
    </ResponsiveModal>
  );
};
